import React, { useCallback, useState } from "react";
import type { MotionProps } from "framer-motion";
import { m as motion, useAnimationControls } from "framer-motion";
import { useTheme } from "@/contexts/theme";
import { cn } from "@/helpers/classNames";

type ThemeSwitcherProps = React.HTMLAttributes<HTMLButtonElement> & MotionProps;

const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({
  className = undefined,
  ...props
}) => {
  const [animationRunning, setAnimationRunning] = useState(false);

  const controls = useAnimationControls();

  const { switchTheme } = useTheme();

  const switchThemeClick = useCallback(() => {
    setAnimationRunning(true);

    if (!animationRunning) {
      controls.start("tap").then(() => {
        controls.start("idle");
        setAnimationRunning(false);
      });
    }

    switchTheme();
  }, [switchTheme, animationRunning, setAnimationRunning, controls]);

  return (
    <motion.button
      aria-label="Farbschema wechseln"
      title="Farbschema wechseln"
      type="button"
      onClick={() => switchThemeClick()}
      onKeyUp={(e) => {
        if (e.key === "Enter") switchThemeClick();
      }}
      animate={controls}
      initial="idle"
      className={cn("rounded group -mr-xs", className)}
      {...props}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        width="68"
        height="28"
        viewBox="0 0 68 28"
        className="w-[3.75rem] sm:w-16 md:w-auto"
      >
        <g
          fill="none"
          fillRule="evenodd"
          style={{ transform: "translateY(2px) translateX(-8px)" }}
        >
          <g
            className={`transition duration-300 ease-out ${
              !animationRunning
                ? "group-hover:-rotate-3 group-hover:-translate-x-[2px]"
                : ""
            }`}
          >
            <motion.polygon
              points="31 2, 31 22, 11 22"
              fill="currentColor"
              fillOpacity="0"
              stroke="currentColor"
              strokeWidth="2.75"
              strokeLinejoin="miter"
              strokeMiterlimit="3"
              fillRule="nonzero"
              variants={{
                idle: {
                  transform: "rotate(0deg)",
                  fillOpacity: 0,
                },
                tap: {
                  transform: "rotate(-15deg)",
                  fillOpacity: 1,
                },
              }}
            />
          </g>
          <g>
            <mask id="b">
              <g
                className={`transition duration-300 ease-out ${
                  !animationRunning
                    ? "group-hover:-rotate-3 group-hover:-translate-x-[2px]"
                    : ""
                }`}
              >
                <motion.polygon
                  points="36 -8, 80 -8, 80 32, 36 32"
                  fill="#fff"
                  variants={{
                    idle: { points: "36 -8, 80 -8, 80 32, 36 32" },
                    tap: { points: "36 -8, 80 -8, 80 32, 11 32" },
                  }}
                />
              </g>
            </mask>
            <motion.circle
              cx="42"
              cy="12"
              r="9"
              fill="currentColor"
              stroke="currentColor"
              fillOpacity="0"
              strokeWidth="2.75"
              mask="url(#b)"
              variants={{
                idle: { r: 9, strokeWidth: 3, translateY: 0, fillOpacity: 0 },
                tap: {
                  r: 5,
                  translateY: [-6, 9],
                  fillOpacity: 1,
                  strokeWidth: 2,
                  transition: {
                    r: {
                      ease: "circOut",
                    },
                    translateY: {
                      repeat: 2,
                      repeatType: "reverse",
                      ease: ["easeIn", "easeOut"],
                      duration: 0.25,
                    },
                  },
                },
              }}
            />
          </g>
          <g>
            <mask id="d">
              <motion.g
                variants={{
                  idle: { translateX: 0 },
                  tap: { translateX: -13 },
                }}
              >
                <rect x="42" y="0" width="64" height="100%" fill="#fff" />
                <circle cx="42" cy="12" r="14" fill="#000" />
              </motion.g>
            </mask>
            <g mask="url(#d)">
              <g
                className={`transition duration-300 ease-out ${
                  !animationRunning ? "group-hover:translate-x-[2px]" : ""
                }`}
              >
                <motion.rect
                  x="53"
                  y="4"
                  width="16"
                  height="16"
                  fill="currentColor"
                  fillOpacity="0"
                  stroke="currentColor"
                  strokeWidth="2.75"
                  style={{
                    transform: "rotate(30deg)",
                    transformOrigin: "61px 12px",
                  }}
                  variants={{
                    idle: {
                      rotate: "30deg",
                      translateY: 0,
                      translateX: 0,
                      fillOpacity: 0,
                    },
                    tap: {
                      rotate: "120deg",
                      translateY: 2,
                      translateX: 2,
                      fillOpacity: 1,
                    },
                  }}
                />
              </g>
            </g>
          </g>
        </g>
      </svg>
    </motion.button>
  );
};

export default ThemeSwitcher;
